import { createTheme, type ThemeOptions } from "@mui/material/styles";
import { rawColor, colors } from "../constants/colors";

export const pastelThemeOptions: ThemeOptions = {
  name: "pastel",
  palette: {
    mode: "light",
    primary: { main: rawColor.pastel.main },
    secondary: { main: rawColor.pastel.secondary },
    background: {
      default: rawColor.pastel.background,
      paper: rawColor.pastel.paper,
    },
    text: {
      primary: rawColor.pastel.text.primary,
      secondary: rawColor.pastel.text.secondary,
    },
    action: {
      hover: rawColor.pastel.sidebar.hover,
    },
    error: { main: rawColor.status.error.light },
    warning: { main: rawColor.status.warning },
    success: { main: rawColor.status.active.green },
    divider: rawColor.pastel.divider,
  },
  sidebar: {
    base: colors.sidebar.pastel.base,
    hover: colors.sidebar.pastel.hover,
    selected: colors.sidebar.pastel.selected,
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.sidebar.base,
          borderRight: `1px solid ${theme.palette.divider}`,
          "& .MuiListItemIcon-root": {
            color: rawColor.pastel.text.secondary,
          },
          "& .MuiListItemText-root": {
            color: rawColor.pastel.text.primary,
          },
          "& .MuiListItemButton-root.Mui-selected": {
            backgroundColor: theme.sidebar.selected,
            "& .MuiListItemIcon-root": {
              color: rawColor.pastel.main,
            },
            "& .MuiListItemText-root": {
              color: rawColor.pastel.main,
            },
          },
          "& .MuiListItemButton-root:hover": {
            backgroundColor: theme.sidebar.hover,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: colors.table.pastel.cell,
          color: rawColor.pastel.text.primary,
        },
        head: {
          backgroundColor: colors.table.pastel.header,
          color: colors.table.pastel.headerText,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: rawColor.pastel.paper,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h5: {
          color: rawColor.pastel.text.primary,
        },
        h6: {
          color: rawColor.pastel.text.primary,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: rawColor.pastel.text.primary,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: rawColor.pastel.text.secondary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "currentColor",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: rawColor.pastel.text.secondary,
          "& .MuiInputBase-input": {
            color: rawColor.pastel.text.secondary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: rawColor.pastel.text.secondary,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.pastel.divider,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.pastel.text.secondary,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.pastel.main,
          },
          "& .MuiOutlinedInput-input": {
            color: rawColor.pastel.text.secondary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: rawColor.pastel.text.secondary,
          "&.Mui-focused": {
            color: rawColor.pastel.main,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: rawColor.pastel.text.secondary,
        },
        select: {
          color: rawColor.pastel.text.secondary,
        },
        selectLabel: {
          color: rawColor.pastel.text.secondary,
        },
        selectIcon: {
          color: rawColor.pastel.text.secondary,
        },
        displayedRows: {
          color: rawColor.pastel.text.secondary,
        },
        actions: {
          color: rawColor.pastel.text.secondary,
          "& .MuiSvgIcon-root": {
            color: rawColor.pastel.text.secondary,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiAutocomplete-option": {
            color: rawColor.pastel.text.secondary,
            "&:hover": {
              backgroundColor: rawColor.pastel.sidebar.hover,
            },
            '&[aria-selected="true"]': {
              backgroundColor: rawColor.pastel.sidebar.selected,
              color: rawColor.pastel.main,
              "&:hover": {
                backgroundColor: rawColor.pastel.sidebar.selected,
              },
            },
          },
          "& .MuiAutocomplete-listbox": {
            backgroundColor: rawColor.pastel.paper,
            "& .MuiAutocomplete-option": {
              padding: "8px 16px",
            },
          },
          "& .MuiAutocomplete-option .MuiTypography-root": {
            color: "inherit",
          },
          "& .MuiAutocomplete-option .MuiListItemText-root": {
            color: "inherit",
          },
        },
        paper: {
          backgroundColor: rawColor.pastel.paper,
          "& .MuiAutocomplete-option": {
            color: rawColor.pastel.text.secondary,
          },
        },
        listbox: {
          "& .MuiAutocomplete-option": {
            color: rawColor.pastel.text.secondary,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        ".select__control": {
          backgroundColor: `${rawColor.pastel.paper} !important`,
          borderColor: `${rawColor.pastel.divider} !important`,
          "&:hover": {
            borderColor: `${rawColor.pastel.text.secondary} !important`,
          },
          "&--is-focused": {
            borderColor: `${rawColor.pastel.main} !important`,
            boxShadow: `0 0 0 1px ${rawColor.pastel.main} !important`,
          },
        },
        ".select__menu": {
          backgroundColor: `${rawColor.pastel.paper} !important`,
          border: `1px solid ${rawColor.pastel.divider} !important`,
        },
        ".select__option": {
          backgroundColor: `${rawColor.pastel.paper} !important`,
          color: `${rawColor.pastel.text.secondary} !important`,
          "&:hover": {
            backgroundColor: `${rawColor.pastel.sidebar.hover} !important`,
          },
          "&--is-focused": {
            backgroundColor: `${rawColor.pastel.sidebar.hover} !important`,
          },
          "&--is-selected": {
            backgroundColor: `${rawColor.pastel.sidebar.selected} !important`,
            color: `${rawColor.pastel.main} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.pastel.sidebar.selected} !important`,
            },
          },
        },
        ".select__multi-value": {
          backgroundColor: `${rawColor.pastel.sidebar.hover} !important`,
          "&__label": {
            color: `${rawColor.pastel.text.secondary} !important`,
          },
          "&__remove": {
            color: `${rawColor.pastel.text.secondary} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.pastel.sidebar.selected} !important`,
              color: `${rawColor.pastel.main} !important`,
            },
          },
        },
        ".select__placeholder": {
          color: `${rawColor.pastel.text.secondary} !important`,
        },
        ".select__single-value": {
          color: `${rawColor.pastel.text.secondary} !important`,
        },
        ".select__input": {
          color: `${rawColor.pastel.text.secondary} !important`,
        },
        ".select__indicator": {
          color: `${rawColor.pastel.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.pastel.main} !important`,
          },
        },
        ".select__indicator-separator": {
          backgroundColor: `${rawColor.pastel.divider} !important`,
        },
        ".select__clear-indicator": {
          color: `${rawColor.pastel.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.pastel.main} !important`,
          },
        },
        ".select__dropdown-indicator": {
          color: `${rawColor.pastel.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.pastel.main} !important`,
          },
        },
      },
    },
  },
};

export const pastelTheme = createTheme(pastelThemeOptions);
